import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { format } from 'date-fns';
import { getDisplayName } from '../../../../models/NameUtils';
import { Button } from '../../../components-v2/Button';
import { Pill } from '../../../components-v2/Pill';
import type { ThemeColors } from '../../../components-v2/Theme/theme';
import { DataGridRow, DataGridRowItem } from '../../DataGrid';
import { blogStatusLabel } from './blogStatusFilters';
import { styleSheet } from './styles';

// Mostly the colours these statuses already used: destructive, success, warningDark and the brand secondary.
// Rejected breaks from destructive on purpose — it means compliance asked for changes, which is a different
// thing to ask of a reader than a post someone cancelled or one that failed to send.
const statusPillColor = (status: Api.BlogStatus): keyof ThemeColors['pill'] => {
	switch (status) {
		case Api.BlogStatus.Rejected:
			return 'pink';
		case Api.BlogStatus.Cancelled:
		case Api.BlogStatus.Failed:
			return 'red';
		case Api.BlogStatus.Completed:
			return 'green';
		case Api.BlogStatus.Pending:
			return 'orange';
		default:
			return 'purple';
	}
};

const NOT_AVAILABLE = 'N/A';

const formatDate = (value?: string) => (value ? format(new Date(value), 'MM/dd/yyyy') : NOT_AVAILABLE);

const formatDateTime = (value?: string) => (value ? format(new Date(value), "MM/dd/yyyy 'at' h:mm a") : NOT_AVAILABLE);

const emailOf = (user?: Api.IUserReference) => user?.primaryEmail?.value || user?.email;

export const BlogReportingTableRow = ({
	isAtLeftOfTable,
	isOpening,
	onTitleClick,
	report,
}: {
	isAtLeftOfTable: boolean;
	isOpening?: boolean;
	onTitleClick?: (report: Api.IBlogReportView) => void;
	report: Api.IBlogReportView;
}) => {
	const authorEmail = emailOf(report.author);
	const title = report.title || 'Untitled';
	return (
		<DataGridRow>
			<DataGridRowItem styles={[styleSheet.titleColumn, !isAtLeftOfTable ? styleSheet.dataCellSticky : undefined]}>
				{onTitleClick ? (
					/* Not `isLoading`: that swaps the title out for a spinner, which reads as a blink on a fast open.
					   `:disabled` already dims and blocks pointer events, so it carries the feedback on a slow one. */
					<Button
						disabled={!report.id || isOpening}
						onClick={() => onTitleClick(report)}
						styles={[styleSheet.titleButton]}
						variant='link'
					>
						{title}
					</Button>
				) : (
					title
				)}
			</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.statusColumn]}>
				<Pill
					color={statusPillColor(report.status)}
					label={blogStatusLabel(report.status)}
					size='small'
					variant='label'
				/>
			</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.dateColumn]}>{formatDate(report.creationDate)}</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.dateTimeColumn]}>{formatDateTime(report.completedDate)}</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.authorColumn]}>
				{report.author ? (
					<>
						<div className={css(styleSheet.authorName)}>{getDisplayName(report.author)}</div>
						{authorEmail ? <div className={css(styleSheet.authorEmail)}>{authorEmail}</div> : null}
					</>
				) : (
					NOT_AVAILABLE
				)}
			</DataGridRowItem>
		</DataGridRow>
	);
};
